import { useEffect, useState } from "react";
import api, { apiError, brl, effectivePrice, fileUrl, printAgent } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Wrench, Pencil, Trash2, Printer, FileText } from "lucide-react";
import { ConfirmDialog, Modal } from "@/components/panel/ui";
import Pagination from "@/components/panel/Pagination";
import usePaginatedList from "@/hooks/usePaginatedList";
import CustomerPicker, { FREE_TEXT } from "@/components/panel/pickers/CustomerPicker";
import ProductPicker, { FREE_TEXT as PRODUCT_FREE_TEXT } from "@/components/panel/pickers/ProductPicker";
import { Badge, Button, DataTable, Field, Input, PageHeader, SearchInput, Textarea } from "@/components/ui-kit";

/**
 * Manda a OS para a impressora em folha, pelo agente local.
 *
 * Duas vias na mesma página (cliente e bancada) — quem decide isso é o PDF do
 * backend. Folha, e não bobina: são medidas em tabela e um campo de assinatura,
 * que numa térmica de 80mm sairiam espremidos.
 */
async function printOS(os) {
  const tid = toast.loading("Enviando OS para a impressora...");
  try {
    await printAgent.printApiPdf(`/service-orders/${os.id}/pdf`);
    toast.success("OS enviada para impressão", { id: tid });
  } catch (e) {
    toast.error(e.message || "Falha ao imprimir", { id: tid });
  }
}

const STATUS = {
  aberta: ["Aberta", "bg-slate-100 text-slate-600"],
  producao: ["Produção", "bg-amber-50 text-amber-700"],
  pronta: ["Pronta", "bg-violet-50 text-violet-700"],
  entregue: ["Entregue", "bg-emerald-50 text-emerald-700"],
  cancelada: ["Cancelada", "bg-red-50 text-red-600"],
};
const FLOW = ["aberta", "producao", "pronta", "entregue", "cancelada"];
/* Mesma progressão do FLOW, em tom semântico do Badge. */
const OS_TONE = { aberta: "neutral", producao: "warning", pronta: "purple", entregue: "success", cancelada: "danger" };
const FILTERS = [["", "Todas"], ["aberta", "Abertas"], ["producao", "Produção"], ["pronta", "Prontas"], ["entregue", "Entregues"]];

const EMPTY = { customer_id: null, customer_name: "", customer_phone: "", product_id: null, variant_id: null, product_ref: "", measurements: [{ label: "", value: "" }], description: "", price: "", deposit: "", due_date: "", assigned_to: "", notes: "" };

function OSForm({ os, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [events, setEvents] = useState([]);
  const [saving, setSaving] = useState(false);
  const [changing, setChanging] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    if (!os) { setForm(EMPTY); return; }
    api.get(`/service-orders/${os.id}`).then(({ data }) => {
      setForm({
        customer_id: data.customer_id, customer_name: data.customer_name || "", customer_phone: data.customer_phone || "",
        product_id: data.product_id, variant_id: data.variant_id,
        product_ref: data.product_ref || "", measurements: data.measurements?.length ? data.measurements : [{ label: "", value: "" }],
        description: data.description || "", price: data.price ?? "", deposit: data.deposit ?? "", due_date: data.due_date ? data.due_date.slice(0, 10) : "", assigned_to: data.assigned_to || "",
        notes: data.notes || "",
      });
      setEvents(data.events || []);
    });
  }, [os]);

  const setMeas = (i, k, v) => setForm({ ...form, measurements: form.measurements.map((m, j) => (j === i ? { ...m, [k]: v } : m)) });

  const save = async () => {
    if (!form.customer_name.trim() || !form.product_ref.trim()) { toast.error("Informe cliente e o produto sob-medida"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        measurements: form.measurements.filter((m) => m.label || m.value),
        price: parseFloat(form.price) || 0, deposit: parseFloat(form.deposit) || 0, due_date: form.due_date || null,
      };
      if (os) await api.patch(`/service-orders/${os.id}`, payload);
      else await api.post("/service-orders", payload);
      toast.success(os ? "OS atualizada" : "OS aberta");
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const changeStatus = async (status) => {
    setChanging(true);
    try { await api.post(`/service-orders/${os.id}/status`, { status }); toast.success("Status atualizado"); onSaved(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setChanging(false); }
  };

  return (
    <Modal open onClose={onClose} title={os ? `OS ${os.number}` : "Nova Ordem de Serviço"} maxWidth="max-w-2xl" testId="os-form-dialog">

        {os && (
          <div className="flex flex-wrap gap-1.5 mb-4" data-testid="os-status-bar">
            {FLOW.map((s) => (
              <button key={s} onClick={() => (s === "cancelada" ? setConfirmCancel(true) : changeStatus(s))} disabled={changing} className={`text-xs font-semibold px-2.5 py-1 rounded-full disabled:opacity-50 ${os.status === s ? STATUS[s][1] + " ring-2 ring-offset-1 ring-brand-300" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>{STATUS[s][0]}</button>
            ))}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Cliente" span required>
<CustomerPicker
                value={form.customer_id || (form.customer_name ? FREE_TEXT : "")}
                selectedLabel={form.customer_name}
                onChange={(c) => setForm({
                  ...form,
                  customer_id: c?.id || null,
                  customer_name: c?.name || "",
                  customer_phone: c?.phone || form.customer_phone,
                })}
                placeholder="Busque um cliente cadastrado ou digite o nome"
                allowFreeText
                testId="os-customer"
              />
</Field>
          <Field label="Telefone"><Input value={form.customer_phone} onChange={set("customer_phone")} /></Field>
          <Field label="Responsável (técnico)"><Input value={form.assigned_to} onChange={set("assigned_to")} /></Field>
          <Field label="Produto sob-medida" span required>
{/* Um campo só: peça que parte do catálogo grava o vínculo e puxa o
                  preço; peça totalmente sob-medida vale pelo que for digitado. */}
              <ProductPicker
                productId={form.product_id || (form.product_ref ? PRODUCT_FREE_TEXT : "")}
                variantId={form.variant_id || ""}
                selectedLabel={form.product_ref}
                onChange={({ product, variant, freeText }) => setForm({
                  ...form,
                  product_id: product?.id || null,
                  variant_id: variant?.id || null,
                  product_ref: product
                    ? `${product.name}${variant && variant.label !== "Único" ? ` (${variant.label})` : ""}`
                    : (freeText ?? ""),
                  // Peça sob-medida tem preço próprio: só sobrescreve quando veio do catálogo.
                  price: product ? effectivePrice(product, variant) : form.price,
                })}
                placeholder="Busque no catálogo ou digite (ex.: Palmilha ortopédica)"
                allowFreeText
                testId="os-product"
              />
</Field>

          {/* Medidas são pares nome/valor repetidos: um <label> solto não
              rotularia nenhum campo. Fieldset nomeia o grupo, e cada linha diz
              qual medida é no próprio nome acessível. */}
          <fieldset className="col-span-2">
            <div className="flex items-center justify-between">
              <legend className="text-eyebrow uppercase tracking-wider text-slate-500">Medidas</legend>
              <Button variant="link" size="sm" icon={Plus}
                onClick={() => setForm({ ...form, measurements: [...form.measurements, { label: "", value: "" }] })}>medida</Button>
            </div>
            {form.measurements.map((m, i) => (
              <div key={i} className="flex gap-2 mt-1.5" data-testid="os-measure-row">
                <Input value={m.label} onChange={(e) => setMeas(i, "label", e.target.value)} placeholder="Ex.: Comprimento pé D"
                  aria-label={`Nome da medida ${i + 1}`} size="sm" className="flex-1 px-2" />
                <Input value={m.value} onChange={(e) => setMeas(i, "value", e.target.value)} placeholder="Valor"
                  aria-label={`Valor da medida ${i + 1}`} size="sm" mono className="w-28 px-2" />
                <Button variant="ghost" size="iconSm" onClick={() => setForm({ ...form, measurements: form.measurements.filter((_, j) => j !== i) })}
                  aria-label={`Remover medida ${i + 1}`} className="hover:text-red-600"><Trash2 size={14} /></Button>
              </div>
            ))}
          </fieldset>

          <Field label="Valor (R$)"><Input type="number" step="0.01" value={form.price} onChange={set("price")} data-testid="os-price" /></Field>
          <Field label="Entrada / sinal (R$)"><Input type="number" step="0.01" value={form.deposit} onChange={set("deposit")} /></Field>
          <Field label="Prazo de entrega"><Input type="date" value={form.due_date} onChange={set("due_date")} /></Field>
          {/* O combinado de pagamento que não cabe em número — "3x no cartão",
              "o resto quando trouxer a receita". Sai no papel logo abaixo do
              total, e só quando tem algo escrito. */}
          <Field label="Observação do pagamento" hint="Sai na OS impressa, abaixo do total" span>
            <Input value={form.notes} onChange={set("notes")} placeholder="Ex.: restante em 2x no cartão na retirada" data-testid="os-payment-notes" />
          </Field>
          <Field label="Descrição / observações" span><Textarea value={form.description} onChange={set("description")} rows={2} /></Field>
        </div>

        {os && events.length > 0 && (
          <div className="mt-4 border-t border-slate-100 pt-3">
            <p className="text-[11px] font-semibold uppercase text-slate-500 mb-2">Histórico</p>
            <div className="space-y-1.5">
              {events.map((ev) => (
                <div key={ev.id} className="text-xs text-slate-500 flex gap-2">
                  <span className="text-slate-400">{new Date(ev.created_at).toLocaleString("pt-BR")}</span>
                  <span>{ev.to_status ? `→ ${STATUS[ev.to_status]?.[0] || ev.to_status}` : ""} {ev.note} {ev.user_name ? `· ${ev.user_name}` : ""}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <button onClick={save} disabled={saving} data-testid="os-save" className="w-full mt-5 bg-brand-600 hover:bg-brand-700 disabled:opacity-40 text-white font-semibold py-2.5 rounded-lg">{saving ? "Salvando..." : os ? "Salvar alterações" : "Abrir OS"}</button>

        <ConfirmDialog open={confirmCancel} onClose={() => setConfirmCancel(false)} onConfirm={() => changeStatus("cancelada")}
          title="Cancelar OS"
          message={`Cancelar a ${os ? `OS ${os.number}` : "ordem de serviço"}? O cliente e o histórico serão mantidos, mas a OS sai da produção. Esta ação não pode ser desfeita.`}
          confirmLabel="Cancelar OS" />
    </Modal>
  );
}

export default function ServiceOrders() {
  const [filter, setFilter] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  // Status e busca já iam para o servidor; o hook assume o debounce da busca e
  // volta para a página 1 sempre que um filtro muda.
  const {
    items, total, page, lastPage, setPage, search, setSearch,
    isLoading: loading, isError: error, refetch: load,
  } = usePaginatedList("/service-orders", filter ? { status: filter } : {});

  return (
    <div className="space-y-5" data-testid="admin-service-orders-page">
      <PageHeader
        eyebrow="Operação"
        title="Ordens de Serviço"
        icon={Wrench}
        description="Peças sob medida — palmilhas, órteses, adaptações. A OS guarda medidas, prazo e o andamento até a entrega."
        actions={(
          <>
            <SearchInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar OS, cliente, produto" data-testid="os-search" aria-label="Buscar ordem de serviço por número, cliente ou produto" width="w-60" />
            <Button icon={Plus} onClick={() => { setEditing(null); setShowForm(true); }} data-testid="os-new-btn">Nova OS</Button>
          </>
        )}
        filters={(
          <div className="flex bg-white border border-slate-200 rounded-xl p-1 w-fit" role="group" aria-label="Filtrar por status">
            {FILTERS.map(([v, l]) => (
              <button key={v || "all"} type="button" onClick={() => setFilter(v)} data-testid={`os-filter-${v || "all"}`}
                aria-pressed={filter === v}
                className={`text-xs font-semibold px-3 h-8 rounded-md transition-colors ${filter === v ? "bg-brand-600 text-white" : "text-slate-500 hover:text-slate-800 hover:bg-slate-50"}`}>{l}</button>
            ))}
          </div>
        )}
      />

      <DataTable
        caption="Ordens de serviço"
        rows={items}
        rowKey={(o) => o.id}
        loading={loading}
        error={error}
        onRetry={load}
        empty={{
          icon: Wrench,
          title: filter ? "Nenhuma OS nesse status" : "Nenhuma ordem de serviço",
          desc: filter
            ? "Troque o filtro para ver as demais."
            : "Abra uma OS para produtos sob-medida (palmilhas, órteses...).",
          action: !filter && <Button icon={Plus} onClick={() => { setEditing(null); setShowForm(true); }}>Abrir primeira OS</Button>,
          testId: "os-empty",
        }}
        columns={[
          { key: "os", header: "OS", mono: true, className: "font-semibold", cell: (o) => o.number },
          {
            key: "cliente", header: "Cliente",
            cell: (o) => (<><p className="text-slate-900">{o.customer_name}</p>{o.customer_phone && <p className="text-xs text-slate-400">{o.customer_phone}</p>}</>),
          },
          { key: "produto", header: "Produto", className: "text-slate-600", cell: (o) => o.product_ref },
          { key: "status", header: "Status", cell: (o) => <Badge tone={OS_TONE[o.status]}>{STATUS[o.status]?.[0]}</Badge> },
          {
            key: "prazo", header: "Prazo", className: "text-slate-500",
            cell: (o) => (o.due_date ? new Date(o.due_date).toLocaleDateString("pt-BR") : "—"),
          },
          { key: "valor", header: "Valor", align: "right", mono: true, cell: (o) => brl(o.price) },
          {
            key: "acoes", header: "Ações", align: "center", className: "whitespace-nowrap",
            cell: (o) => (
              <div className="inline-flex items-center gap-1">
                <Button variant="ghost" size="iconSm" onClick={() => { setEditing(o); setShowForm(true); }}
                  data-testid="os-edit-btn" aria-label={`Editar OS ${o.number}`} className="hover:text-brand-600"><Pencil size={15} /></Button>
                <Button variant="ghost" size="iconSm" onClick={() => printOS(o)}
                  data-testid="os-print-btn" aria-label={`Imprimir OS ${o.number}`} title="Imprimir (2 vias)"
                  className="hover:text-brand-600"><Printer size={15} /></Button>
                {/* Sem agente instalado, a OS abre no navegador e sai pelo
                    Ctrl+P — a bancada não pode ficar sem a medida por causa de
                    software que não foi instalado naquela máquina. */}
                <a href={fileUrl(`/service-orders/${o.id}/pdf`)} target="_blank" rel="noreferrer"
                  data-testid="os-pdf-btn" aria-label={`Abrir OS ${o.number} em PDF`} title="Abrir em PDF"
                  className="inline-flex items-center justify-center h-8 w-8 rounded-lg text-brand-600 hover:bg-brand-50"><FileText size={15} /></a>
              </div>
            ),
          },
        ]}
      />
      <Pagination page={page} lastPage={lastPage} total={total} onChange={setPage} label="ordens de serviço" />

      {showForm && <OSForm os={editing} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
    </div>
  );
}
